import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  IsoDate,
  ObjectiveDateSuggestionView,
  ObjectiveForecastView,
  ObjectiveSummaryView,
  ObjectiveView,
  WeekStart,
} from '@contracts';
import { useDomainApi, useDomainVersion } from '@ui/shared/domain/DomainApiContext';
import { Plus, Target, ArrowRight, ArrowUpRight, Check } from '@ui/shared/icons';
import { SkeletonBlock } from '@ui/shared/primitives/Skeleton';
import { formatShortDate, formatKicker, localIsoDate } from '@ui/shared/format/date';
import { getStoredOwnerName, OWNER_NAME_CHANGE_EVENT } from '@ui/shared/settings/ownerName';
import { weekOf, isInWeek } from '@domain/habits/week';

/**
 * Dashboard — a genuinely separate desktop destination
 * (docs/specs/phase-4-dashboard-redesign.md §3's content, RELOCATED here per
 * the owner's direct live-review correction: the previous round bolted this
 * summary layer onto `ObjectivesScreen.desktop.tsx`, keeping "Objectives" as
 * the screen's identity. That was wrong — Dashboard is now its own rail
 * destination and the new desktop default landing route;
 * `ObjectivesScreen.desktop.tsx` has been reverted to the plain
 * active/archived management grid it was before that round).
 *
 * Owns: the real-date + time-of-day greeting header (with the owner's
 * optional display name, `src/ui/shared/settings/ownerName.ts`), the
 * stat-card strip, the "Objectives in flight" panel (pace bar/status pill/
 * insight line per row, reusing the exact `ObjectiveForecastView`-derived
 * verdict-prose construction already built and tested), the realism
 * forecast side panel (reusing the existing `acceptObjectiveDateSuggestion`
 * flow), and the Upcoming list. All of this is a RELOCATION of
 * already-correct, already-tested logic — not a rewrite.
 */
export function DashboardScreenDesktop({
  onOpenObjective,
  onCreateObjective,
  today: todayOverride,
  now: nowOverride,
}: {
  readonly onOpenObjective: (objectiveId: string) => void;
  readonly onCreateObjective: () => void;
  /**
   * Testability override, mirroring `TodayView.tsx`'s own `today` prop
   * pattern: the "actions this week" stat is a client-side reduce over
   * already-fetched `ObjectiveView[]` using device-local "today"
   * (ADR-0006), which this screen has no other way to pin to a fixture's
   * clock in tests without this seam. Defaults to the real device-local
   * date in production, exactly like `TodayView`'s `today` prop.
   */
  readonly today?: IsoDate;
  /** Testability override for the header's real-clock date/greeting (the
   *  one piece of this screen that reads wall-clock time, not domain
   *  "today"), mirroring the `today` override's own reasoning. Defaults to
   *  `new Date()` in production. */
  readonly now?: Date;
}) {
  const api = useDomainApi();
  const version = useDomainVersion();
  const today = todayOverride ?? (localIsoDate() as IsoDate);
  const now = nowOverride ?? new Date();
  const [objectives, setObjectives] = useState<readonly ObjectiveSummaryView[] | null>(null);
  const [detailById, setDetailById] = useState<ReadonlyMap<string, ObjectiveView>>(new Map());
  const [lastLoadedAt, setLastLoadedAt] = useState<number | null>(null);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [ownerName, setOwnerNameState] = useState(() => getStoredOwnerName());

  const load = useCallback(async () => {
    const list = await api.listObjectives({ includeArchived: false });
    setObjectives(list);

    const active = list.filter((o) => o.state === 'active');
    const details = await Promise.all(active.map((o) => api.getObjective(o.objectiveId)));
    setDetailById(new Map(details.map((d) => [d.objectiveId, d])));
    setLastLoadedAt(Date.now());
  }, [api]);

  useEffect(() => {
    void load();
  }, [load, version]);

  // Freshness indicator: a quiet client-side "recalculated Xm ago" fact,
  // ticking once a minute so the text stays current without a reload —
  // purely presentational, no server timestamp, no new domain concept.
  useEffect(() => {
    const id = window.setInterval(() => setNowTick(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  // Owner display-name setting: subscribe so a change made on Settings
  // (a sibling screen) is picked up without a shared context/provider,
  // mirroring ThemeIndicatorDot's own THEME_CHANGE_EVENT subscription.
  useEffect(() => {
    function handleNameChange() {
      setOwnerNameState(getStoredOwnerName());
    }
    window.addEventListener(OWNER_NAME_CHANGE_EVENT, handleNameChange);
    return () => window.removeEventListener(OWNER_NAME_CHANGE_EVENT, handleNameChange);
  }, []);

  const active = useMemo(() => objectives?.filter((o) => o.state === 'active') ?? [], [objectives]);

  const activeDetails = useMemo(
    () => active.map((o) => detailById.get(o.objectiveId)).filter((d): d is ObjectiveView => d !== undefined),
    [active, detailById],
  );

  return (
    <main
      aria-label="Dashboard"
      style={{
        padding: 'var(--space-8) var(--layout-page-margin-desktop) var(--space-16)',
        // Wider than this app's other desktop screens (960-1040px) by
        // deliberate choice: those are reading-width-capped content (forms,
        // lists); Dashboard is a dense data-overview surface, meant to use
        // real desktop width rather than cap to a comfortable line length.
        maxWidth: '1600px',
        margin: '0 auto',
      }}
    >
      <DashboardHeader now={now} ownerName={ownerName} onCreateObjective={onCreateObjective} />

      {objectives === null ? (
        <div style={{ marginTop: 'var(--space-6)', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--space-5)' }}>
          <SkeletonBlock height={220} />
          <SkeletonBlock height={220} />
        </div>
      ) : (
        <>
          <StatCardStrip active={active} activeDetails={activeDetails} today={today} />

          <div style={{ marginTop: 'var(--space-6)', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 380px', gap: 'var(--space-6)', alignItems: 'flex-start' }}>
            <ObjectivesInFlightPanel
              activeDetails={activeDetails}
              onOpenObjective={onOpenObjective}
              lastLoadedAt={lastLoadedAt}
              nowTick={nowTick}
            />

            <aside style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
              <RealismForecastPanel objectives={activeDetails} onOpenObjective={onOpenObjective} onChanged={() => void load()} />
              <UpcomingList objectives={activeDetails} onOpenObjective={onOpenObjective} />
            </aside>
          </div>
        </>
      )}
    </main>
  );
}

// ---------------------------------------------------------------------------
// Header — real date + time-of-day greeting + owner name + New objective
// ---------------------------------------------------------------------------

function greetingFor(now: Date): string {
  const hour = now.getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

const VIEW_TOGGLES = ['This week', 'Month', 'Quarter'] as const;

/**
 * Header layout (owner direct correction from live review): date + greeting
 * are LEFT-aligned, immediately following the sidebar — not right-aligned as
 * the previous round had it. The "Compass" wordmark/level-identity block
 * moved OUT of this header entirely; it's now `SidebarIdentity` at the top
 * of `DesktopShell`'s nav, a persistent fixture across every desktop screen,
 * not a Dashboard-only display (matching the reference's own sidebar, and
 * `LevelFooterChip`'s existing precedent as an always-visible ambient
 * fixture rather than a page-specific header element).
 */
function DashboardHeader({
  now,
  ownerName,
  onCreateObjective,
}: {
  readonly now: Date;
  readonly ownerName: string;
  readonly onCreateObjective: () => void;
}) {
  const [activeToggle, setActiveToggle] = useState<(typeof VIEW_TOGGLES)[number]>('This week');
  const greeting = ownerName ? `${greetingFor(now)}, ${ownerName}.` : `${greetingFor(now)}.`;

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-6)', flexWrap: 'wrap' }}>
      <div>
        <div style={{ fontSize: 'var(--type-caption-size)', fontWeight: 600, letterSpacing: 'var(--type-caption-tracking)', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
          {formatKicker(now)}
        </div>
        <h1 style={{ margin: 0, marginTop: 'var(--space-1)', fontSize: 'var(--type-display-size)', fontWeight: 'var(--type-display-weight)', color: 'var(--text-primary)' }}>
          {greeting}
        </h1>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', flex: 'none' }}>
        <div role="group" aria-label="View range" style={{ display: 'flex', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
          {VIEW_TOGGLES.map((toggle) => (
            <button
              key={toggle}
              type="button"
              aria-pressed={activeToggle === toggle}
              onClick={() => setActiveToggle(toggle)}
              style={{
                padding: '0 var(--space-3)',
                minHeight: 'var(--touch-target-min)',
                fontSize: 'var(--type-caption-size)',
                fontWeight: activeToggle === toggle ? 600 : 500,
                color: activeToggle === toggle ? 'var(--text-primary)' : 'var(--text-secondary)',
                background: activeToggle === toggle ? 'var(--surface-hover)' : 'transparent',
                borderRight: toggle !== VIEW_TOGGLES[VIEW_TOGGLES.length - 1] ? '1px solid var(--border-subtle)' : 'none',
              }}
            >
              {toggle}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={onCreateObjective}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
            minHeight: 'var(--touch-target-min)',
            padding: '0 var(--space-4)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--hue-primary-vivid)',
            border: '1px solid var(--hue-primary-strong)',
            color: 'var(--text-on-accent)',
            fontWeight: 600,
            fontSize: 'var(--type-body-size)',
            flex: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          <Plus size={16} aria-hidden="true" />
          New objective
        </button>
      </div>
    </div>
  );
}

/** Shared by `ObjectivesInFlightPanel`'s header — a live-pulsing dot plus
 *  "Recalculated Xm ago", matching the reference's freshness indicator.
 *  `null` while the first load hasn't completed yet. */
function freshnessText(lastLoadedAt: number | null, nowTick: number): string | null {
  if (lastLoadedAt === null) return null;
  const minutesAgo = Math.floor((nowTick - lastLoadedAt) / 60_000);
  return minutesAgo <= 0 ? 'Recalculated just now' : `Recalculated ${minutesAgo}m ago`;
}

// ---------------------------------------------------------------------------
// Stat-card strip
// ---------------------------------------------------------------------------

function StatCardStrip({
  active,
  activeDetails,
  today,
}: {
  readonly active: readonly ObjectiveSummaryView[];
  readonly activeDetails: readonly ObjectiveView[];
  readonly today: IsoDate;
}) {
  const forecastById = new Map(activeDetails.map((d) => [d.objectiveId, d.forecast]));

  let onPace = 0;
  let atRisk = 0;
  for (const obj of active) {
    const forecast = forecastById.get(obj.objectiveId);
    if (!forecast || forecast.paceActionsPerWeek === null) continue; // no data counts toward neither bucket.
    if (forecast.isSlipping) atRisk += 1;
    else onPace += 1;
  }

  const currentWeekStart = weekOf(today);
  let actionsDone = 0;
  let actionsOpen = 0;
  for (const detail of activeDetails) {
    for (const action of detail.actions) {
      if (action.completed && action.completedOn && isInWeek(action.completedOn, currentWeekStart)) {
        actionsDone += 1;
      } else if (!action.completed) {
        actionsOpen += 1;
      }
    }
  }

  return (
    <div
      aria-label="Objective stats"
      style={{
        marginTop: 'var(--space-6)',
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        gap: 'var(--space-4)',
      }}
    >
      <StatCard label="Active objectives" value={String(active.length)} caption={active.length === 0 ? 'Create your first' : `${active.filter((o) => forecastById.get(o.objectiveId)?.paceActionsPerWeek === null).length} without pace data yet`} />
      <StatCard label="On pace" value={String(onPace)} caption={`of ${active.length}`} color="var(--hue-primary)" />
      <StatCard label="At risk" value={String(atRisk)} caption={atRisk > 0 ? 'shift suggested' : 'none right now'} color={atRisk > 0 ? 'var(--state-caution)' : undefined} />
      <StatCard label="Actions this week" value={`${actionsDone} / ${actionsDone + actionsOpen}`} caption={`${actionsDone} done · ${actionsOpen} open`} color="var(--hue-secondary)" />
    </div>
  );
}

function StatCard({
  label,
  value,
  caption,
  color,
}: {
  readonly label: string;
  readonly value: string;
  readonly caption: string;
  readonly color?: string;
}) {
  return (
    <div
      role="group"
      aria-label={`${label} stat`}
      style={{
        position: 'relative',
        padding: 'var(--space-4)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-subtle)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 'var(--type-label-size)', fontWeight: 'var(--type-label-weight)', letterSpacing: 'var(--type-label-tracking)', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
          {label}
        </div>
        <ArrowUpRight size={14} color="var(--text-muted)" aria-hidden="true" />
      </div>
      <div style={{ marginTop: 'var(--space-2)', display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)' }}>
        <span className="num" style={{ fontSize: 'var(--type-display-size)', fontWeight: 'var(--type-display-weight)', color: color ?? 'var(--text-primary)' }}>
          {value}
        </span>
        <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>{caption}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pace bar + status pill + insight line
// ---------------------------------------------------------------------------

type PaceStatus = 'on_pace' | 'shift_target' | 'no_data';

function paceStatusOf(forecast: ObjectiveForecastView | null): PaceStatus {
  if (!forecast || forecast.paceActionsPerWeek === null) return 'no_data';
  return forecast.isSlipping ? 'shift_target' : 'on_pace';
}

/** Same tone rule as objective-detail.md's realism card: neutral-to-warm,
 *  never red/shame-toned, for the "shift target" state. */
function paceStatusColor(status: PaceStatus): string {
  switch (status) {
    case 'on_pace':
      return 'var(--hue-primary)';
    case 'shift_target':
      return 'var(--state-caution)';
    case 'no_data':
      return 'var(--text-muted)';
  }
}

function paceStatusLabel(status: PaceStatus): string {
  switch (status) {
    case 'on_pace':
      return 'On pace';
    case 'shift_target':
      return 'Shift target';
    case 'no_data':
      return 'Not enough data yet';
  }
}

/**
 * The exact verdict-prose construction from objective-detail.md §1.3/§1.3a
 * (`RealismProjectionSection`), condensed to one sentence for a summary row.
 * Same sentence templates, same numbers, no new copy engine.
 */
function insightSentence(objective: {
  readonly forecast: ObjectiveForecastView;
  readonly progress: { readonly done: number; readonly total: number };
  readonly targetDate: string;
}): string | null {
  const { forecast, progress, targetDate } = objective;
  if (progress.total > 0 && progress.done === progress.total) return 'All modules complete.';
  if (forecast.paceActionsPerWeek === null || forecast.forecastFinishDate === null) {
    return 'Forecast appears once there’s a couple weeks of action history.';
  }
  if (!forecast.isSlipping) {
    const daysEarly = daysBetween(targetDate, forecast.forecastFinishDate);
    return `Pace is steady, ${daysEarly} days early.`;
  }
  return `At ${forecast.paceActionsPerWeek} actions/wk you'll land past target — a gentler runway keeps this on track.`;
}

/** Whole days from b to a (a - b): positive when a is later than b. Mirrors
 *  ObjectiveDetailScreen.tsx's own daysBetween exactly. */
function daysBetween(a: string, b: string): number {
  return Math.round((Date.parse(a) - Date.parse(b)) / 86_400_000);
}

function PaceBar({ done, total, status }: { readonly done: number; readonly total: number; readonly status: PaceStatus }) {
  const percent = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div
      role="img"
      aria-label={`${done} of ${total} actions complete`}
      style={{
        height: 'var(--space-2)',
        background: 'var(--border-subtle)',
        borderRadius: 'var(--radius-pill)',
        marginTop: 'var(--space-3)',
        overflow: 'hidden',
      }}
    >
      <div
        aria-hidden="true"
        style={{
          height: '100%',
          width: `${percent}%`,
          background: paceStatusColor(status),
          borderRadius: 'var(--radius-pill)',
          transition: 'width var(--dur-counter) var(--ease-standard)',
        }}
      />
    </div>
  );
}

function StatusPill({ status }: { readonly status: PaceStatus }) {
  return (
    <span
      style={{
        fontSize: 'var(--type-caption-size)',
        fontWeight: 600,
        color: paceStatusColor(status),
        background: 'var(--surface-hover)',
        padding: '2px 10px',
        borderRadius: 'var(--radius-pill)',
        whiteSpace: 'nowrap',
      }}
    >
      {paceStatusLabel(status)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// "Objectives in flight" panel
// ---------------------------------------------------------------------------

function ObjectivesInFlightPanel({
  activeDetails,
  onOpenObjective,
  lastLoadedAt,
  nowTick,
}: {
  readonly activeDetails: readonly ObjectiveView[];
  readonly onOpenObjective: (objectiveId: string) => void;
  readonly lastLoadedAt: number | null;
  readonly nowTick: number;
}) {
  const freshness = freshnessText(lastLoadedAt, nowTick);

  return (
    <section aria-label="Objectives in flight">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <h2 style={{ margin: 0, fontSize: 'var(--type-heading-size)', fontWeight: 'var(--type-heading-weight)', color: 'var(--text-primary)' }}>
            Objectives in flight
          </h2>
          <span
            style={{
              fontSize: 'var(--type-label-size)',
              fontWeight: 'var(--type-label-weight)',
              letterSpacing: 'var(--type-label-tracking)',
              textTransform: 'uppercase',
              color: 'var(--hue-primary)',
              background: 'var(--surface-hover)',
              padding: '2px 10px',
              borderRadius: 'var(--radius-pill)',
              whiteSpace: 'nowrap',
            }}
          >
            Realism engine · Live
          </span>
        </div>
        {freshness && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <span
              aria-hidden="true"
              style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--hue-primary-vivid)', flex: 'none' }}
            />
            <span style={{ fontSize: 'var(--type-caption-size)', letterSpacing: 'var(--type-caption-tracking)', textTransform: 'uppercase', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
              {freshness}
            </span>
          </div>
        )}
      </div>

      <div style={{ marginTop: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {activeDetails.length === 0 ? (
          <div
            style={{
              padding: 'var(--space-6)',
              borderRadius: 'var(--radius-xl)',
              background: 'var(--surface-raised)',
              border: '1px dashed var(--border-subtle)',
              textAlign: 'center',
            }}
          >
            <p style={{ margin: 0, fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>
              Objectives give your habits somewhere to point. One or two is plenty.
            </p>
          </div>
        ) : (
          activeDetails.map((obj) => (
            <ObjectiveInFlightRow key={obj.objectiveId} objective={obj} onSelect={() => onOpenObjective(obj.objectiveId)} />
          ))
        )}
      </div>
    </section>
  );
}

function ObjectiveInFlightRow({
  objective,
  onSelect,
}: {
  readonly objective: ObjectiveView;
  readonly onSelect: () => void;
}) {
  const complete = objective.progress.total > 0 && objective.progress.done === objective.progress.total;
  const status = paceStatusOf(objective.forecast);

  return (
    <div
      style={{
        padding: 'var(--space-5)',
        borderRadius: 'var(--radius-xl)',
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-subtle)',
        boxShadow: 'var(--shadow-1)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        <span
          aria-hidden="true"
          style={{
            width: '44px',
            height: '44px',
            borderRadius: 'var(--radius-md)',
            background: 'color-mix(in srgb, var(--hue-primary-vivid) 16%, var(--surface-raised))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 'none',
          }}
        >
          <Target size={20} color="var(--hue-primary)" aria-hidden="true" />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <button
            type="button"
            onClick={onSelect}
            style={{ display: 'block', textAlign: 'left', fontSize: 'var(--type-heading-size)', fontWeight: 600, color: 'var(--text-primary)' }}
          >
            {objective.title}
          </button>
          <div style={{ marginTop: '2px', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>
            {objective.targetDate ? `Target ${formatShortDate(objective.targetDate)}` : 'Ongoing'}
            {!complete && objective.forecast.forecastFinishDate && ` · Forecast ${formatShortDate(objective.forecast.forecastFinishDate)}`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)', flex: 'none', alignItems: 'center' }}>
          {complete && (
            <span
              style={{
                fontSize: 'var(--type-caption-size)',
                fontWeight: 600,
                color: 'var(--hue-primary)',
                background: 'var(--surface-hover)',
                padding: '2px 10px',
                borderRadius: 'var(--radius-pill)',
              }}
            >
              Complete
            </span>
          )}
          {!complete && <StatusPill status={status} />}
          <button
            type="button"
            onClick={onSelect}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-1)',
              fontSize: 'var(--type-caption-size)',
              fontWeight: 600,
              color: 'var(--hue-primary)',
              whiteSpace: 'nowrap',
            }}
          >
            Open <ArrowRight size={14} color="var(--hue-primary-vivid)" aria-hidden="true" />
          </button>
        </div>
      </div>

      <div style={{ marginTop: 'var(--space-4)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-2)' }}>
        <div className="num" style={{ fontSize: 'var(--type-caption-size)', color: 'var(--hue-primary)', fontWeight: 600 }}>
          {objective.progress.done} / {objective.progress.total} complete
        </div>
      </div>
      <PaceBar done={objective.progress.done} total={objective.progress.total} status={status} />
      {!complete && (
        <p style={{ marginTop: 'var(--space-2)', fontSize: 'var(--type-caption-size)', lineHeight: 1.4, color: 'var(--text-secondary)' }}>
          {insightSentence({ forecast: objective.forecast, progress: objective.progress, targetDate: objective.targetDate })}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Realism forecast side panel
// ---------------------------------------------------------------------------

function pickMostAtRisk(objectives: readonly ObjectiveView[]): ObjectiveView | null {
  const slipping = objectives.filter((o) => o.forecast.isSlipping && o.forecast.forecastFinishDate !== null);
  if (slipping.length === 0) return null;
  // Tie-break (not load-bearing): largest slip in days — forecastFinishDate
  // furthest past targetDate first.
  return slipping.reduce((worst, candidate) => {
    const worstSlip = daysBetween(worst.forecast.forecastFinishDate as string, worst.targetDate);
    const candidateSlip = daysBetween(candidate.forecast.forecastFinishDate as string, candidate.targetDate);
    return candidateSlip > worstSlip ? candidate : worst;
  });
}

function RealismForecastPanel({
  objectives,
  onOpenObjective,
  onChanged,
}: {
  readonly objectives: readonly ObjectiveView[];
  readonly onOpenObjective: (objectiveId: string) => void;
  readonly onChanged: () => void;
}) {
  const api = useDomainApi();
  const target = useMemo(() => pickMostAtRisk(objectives), [objectives]);

  return (
    <section
      aria-label="Realism forecast"
      style={{
        padding: 'var(--space-4)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-subtle)',
      }}
    >
      <PanelLabel>Realism forecast</PanelLabel>

      {target === null ? (
        <p style={{ marginTop: 'var(--space-3)', fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>
          {objectives.length === 0 ? 'Nothing to forecast yet — create an objective to see it here.' : "Everything's on pace."}
        </p>
      ) : (
        <>
          <button
            type="button"
            onClick={() => onOpenObjective(target.objectiveId)}
            style={{ display: 'block', textAlign: 'left', marginTop: 'var(--space-3)', fontSize: 'var(--type-body-size)', fontWeight: 600, color: 'var(--text-primary)' }}
          >
            {target.title}
          </button>
          <p style={{ marginTop: 'var(--space-2)', fontSize: 'var(--type-caption-size)', lineHeight: 1.5, color: 'var(--text-secondary)' }}>
            {insightSentence(target)}
          </p>
          {target.forecast.suggestion && (
            <AdjustmentOfferCard
              objectiveId={target.objectiveId}
              currentTargetDate={target.targetDate}
              suggestion={target.forecast.suggestion}
              onApply={async () => {
                const suggestion: ObjectiveDateSuggestionView = {
                  objectiveId: target.objectiveId,
                  currentTargetDate: target.targetDate,
                  suggestedTargetDate: target.forecast.suggestion!.suggestedTargetDate,
                  requiredPaceActionsPerWeek: target.forecast.suggestion!.requiredPaceActionsPerWeek,
                  reviewWeek: currentWeekStart(),
                };
                await api.acceptObjectiveDateSuggestion(suggestion);
                onChanged();
              }}
            />
          )}
        </>
      )}
    </section>
  );
}

function PanelLabel({ children }: { readonly children: string }) {
  return (
    <div
      style={{
        fontSize: 'var(--type-caption-size)',
        fontWeight: 600,
        letterSpacing: 'var(--type-caption-tracking)',
        textTransform: 'uppercase',
        color: 'var(--hue-primary)',
      }}
    >
      {children}
    </div>
  );
}

/**
 * The exact inline-card-with-Apply-link composition already built and
 * shipped for Objective Detail (objective-detail.md §1.3, reused verbatim
 * at panel scale — same visual anatomy, same `acceptObjectiveDateSuggestion`
 * flow, no new interaction pattern).
 */
function AdjustmentOfferCard({
  suggestion,
  onApply,
}: {
  readonly objectiveId: string;
  readonly currentTargetDate: string;
  readonly suggestion: { readonly suggestedTargetDate: string; readonly requiredPaceActionsPerWeek: number };
  readonly onApply: () => Promise<void>;
}) {
  const [applied, setApplied] = useState(false);

  if (applied) {
    return (
      <div style={{ marginTop: 'var(--space-3)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--type-caption-size)', color: 'var(--text-primary)' }}>
        <Check size={14} color="var(--hue-secondary)" aria-hidden="true" />
        Target moved to {formatShortDate(suggestion.suggestedTargetDate)}.
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Move target to ${formatShortDate(suggestion.suggestedTargetDate)}, keeps this within reach`}
      onClick={() => {
        void onApply().then(() => setApplied(true));
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          void onApply().then(() => setApplied(true));
        }
      }}
      style={{
        marginTop: 'var(--space-3)',
        background: 'var(--surface-hover)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-3)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--space-2)',
        cursor: 'pointer',
      }}
    >
      <div>
        <div style={{ fontSize: 'var(--type-caption-size)', fontWeight: 600, color: 'var(--text-primary)' }}>
          Move target to {formatShortDate(suggestion.suggestedTargetDate)}
        </div>
        <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', marginTop: 'var(--space-1)' }}>
          keeps this within reach
        </div>
      </div>
      <span style={{ fontSize: 'var(--type-caption-size)', fontWeight: 600, color: 'var(--hue-primary)', display: 'flex', alignItems: 'center', gap: 'var(--space-1)', flex: 'none' }}>
        Apply <ArrowRight size={14} color="var(--hue-primary-vivid)" aria-hidden="true" />
      </span>
    </div>
  );
}

/**
 * DOMAIN-API GAP (same one flagged in ObjectiveDetailScreen.tsx): WeekStart
 * is a branded type; `getCurrentReviewWeek()` is documented as the
 * weekly-review-presentation week, not necessarily "Monday of the calendar
 * week containing today" needed for `ObjectiveDateSuggestionView.reviewWeek`
 * when accepting a forecast suggestion outside the weekly review. This
 * duplicates ObjectiveDetailScreen.tsx's own local placeholder rather than
 * inventing a second, different one — both should be replaced by a real
 * `getCurrentWeekStart()` domain addition.
 */
function currentWeekStart(): WeekStart {
  const now = new Date();
  const day = now.getDay();
  const diff = (day + 6) % 7;
  now.setDate(now.getDate() - diff);
  return localIsoDate(now) as WeekStart;
}

// ---------------------------------------------------------------------------
// Upcoming (nearest dated actions)
// ---------------------------------------------------------------------------

interface UpcomingEntry {
  readonly actionId: string;
  readonly title: string;
  readonly dueDate: string;
  readonly objectiveId: string;
  readonly objectiveTitle: string;
}

function buildUpcoming(objectives: readonly ObjectiveView[]): readonly UpcomingEntry[] {
  const entries: UpcomingEntry[] = [];
  for (const obj of objectives) {
    for (const action of obj.actions) {
      if (action.completed || action.dueDate === null) continue;
      entries.push({
        actionId: action.actionId,
        title: action.title,
        dueDate: action.dueDate,
        objectiveId: obj.objectiveId,
        objectiveTitle: obj.title,
      });
    }
  }
  entries.sort((a, b) => a.dueDate.localeCompare(b.dueDate));
  return entries.slice(0, 5);
}

function UpcomingList({
  objectives,
  onOpenObjective,
}: {
  readonly objectives: readonly ObjectiveView[];
  readonly onOpenObjective: (objectiveId: string) => void;
}) {
  const upcoming = useMemo(() => buildUpcoming(objectives), [objectives]);

  return (
    <section
      aria-label="Upcoming"
      style={{
        padding: 'var(--space-4)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-subtle)',
      }}
    >
      <PanelLabelMuted>Upcoming</PanelLabelMuted>
      {upcoming.length === 0 ? (
        <p style={{ marginTop: 'var(--space-3)', fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)' }}>
          No dated actions coming up.
        </p>
      ) : (
        <ul style={{ marginTop: 'var(--space-3)', listStyle: 'none', padding: 0, margin: 0 }}>
          {upcoming.map((entry, i) => (
            <li
              key={entry.actionId}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
                padding: 'var(--space-2) 0',
                borderTop: i === 0 ? 'none' : '1px solid var(--border-subtle)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-2)' }}>
                <span style={{ fontSize: 'var(--type-caption-size)', fontWeight: 500, color: 'var(--text-primary)' }}>
                  {entry.title}
                </span>
                <span className="num" style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', flex: 'none' }}>
                  {formatShortDate(entry.dueDate)}
                </span>
              </div>
              <button
                type="button"
                onClick={() => onOpenObjective(entry.objectiveId)}
                style={{ textAlign: 'left', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}
              >
                {entry.objectiveTitle}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function PanelLabelMuted({ children }: { readonly children: string }) {
  return (
    <div
      style={{
        fontSize: 'var(--type-caption-size)',
        fontWeight: 600,
        letterSpacing: 'var(--type-caption-tracking)',
        textTransform: 'uppercase',
        color: 'var(--text-muted)',
      }}
    >
      {children}
    </div>
  );
}
